import {
  clearAccessToken,
  readAccessToken,
} from "@/domain/admin/utils/AdminTokenUtils"
import { API_BASE } from "@/config/AppConfig"

/** 401 — 인증 실패/만료 (전 엔드포인트 공통) */
export class UnauthorizedError extends Error {
  constructor() {
    super("인증이 필요하거나 로그인 세션이 만료되었습니다.")
    this.name = "UnauthorizedError"
  }
}

/** 백엔드가 내려주는 공통 결과 페이로드 */
interface ApiResult {
  isSuccess: boolean
  code: string
  message: string
}

interface ApiErrorBody {
  code?: string
  message?: string
  result?: ApiResult
}

interface ApiRequestOptions {
  headers?: HeadersInit
  signal?: AbortSignal
}

class ApiError extends Error {
  readonly status: number
  readonly code: string

  constructor(status: number, code: string, message: string) {
    super(message)
    this.name = "ApiError"
    this.status = status
    this.code = code
  }
}

/** 정제할 수 없는 메시지를 대신할 문구 */
const FALLBACK_ERROR_MESSAGE =
  "요청을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요."

/** 서버 로그 문구는 `=== 무엇이 틀렸다 - 키: 값` 형식이다 */
const SERVER_LOG_MESSAGE_PATTERN = /^===/

/**
 * 백엔드 에러 메시지에서 내부 정보를 제거한다.
 *
 * 서버는 화면용 문구만 내려주기로 되어 있다(`ApiResponseMessageMapper`).
 * 그래도 로그 문구가 새어 나오면 사용자가 읽을 수 없고 내부 값이 드러나므로 통째로 바꾼다.
 * - "EntryRegisterHandler 접수에 실패했습니다." → "접수에 실패했습니다."
 * - "=== 미취학 아동은 참가 대상이 아니다 - birthDate: 2026-08-11, grade: -6" → 기본 문구
 */
function sanitizeErrorMessage(message: string): string {
  const withoutHandlerName = message.replace(/\b\w+Handler\b\s*/gi, "").trim()
  const isServerLogMessage = SERVER_LOG_MESSAGE_PATTERN.test(withoutHandlerName)
  if (isServerLogMessage) return FALLBACK_ERROR_MESSAGE

  return withoutHandlerName || FALLBACK_ERROR_MESSAGE
}

/**
 * gRPC-HTTP transcoding 백엔드 공통 POST 클라이언트.
 *
 * - 모든 엔드포인트는 POST + JSON 이다.
 * - 실패는 HTTP 상태가 아니라 `result.isSuccess` 로 판정한다.
 */
async function postJson<Response>(
  path: string,
  bodyJson: unknown,
): Promise<Response> {
  return requestJson<Response>(path, { method: "POST", body: bodyJson })
}

/** REST API 공통 요청기. Page가 아닌 Domain Client에서만 호출한다. */
async function requestJson<Response>(
  path: string,
  options: ApiRequestOptions & {
    method?: "GET" | "POST" | "PUT" | "DELETE"
    body?: unknown
  } = {},
): Promise<Response> {
  const headers = new Headers(options.headers)
  headers.set("Accept", "application/json")

  if (options.body !== undefined)
    headers.set("Content-Type", "application/json")

  // 관리자 API("/bo/*")는 이 헤더가 없으면 서버가 401 로 끊는다
  const accessToken = readAccessToken()
  if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`)

  const response = await fetch(`${API_BASE}${path}`, {
    method: options.method ?? "GET",
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    signal: options.signal,
  })

  if (response.status === 401) {
    // 만료된 토큰을 들고 계속 요청하지 않도록 여기서 버린다
    clearAccessToken()
    throw new UnauthorizedError()
  }
  if (response.status === 204) return undefined as Response

  const contentType = response.headers.get("content-type") ?? ""
  const body = contentType.includes("application/json")
    ? ((await response.json()) as ApiErrorBody & Response)
    : undefined

  if (!response.ok) {
    throw new ApiError(
      response.status,
      body?.result?.code ?? body?.code ?? "HTTP_ERROR",
      sanitizeErrorMessage(
        body?.result?.message ??
          body?.message ??
          `${path} failed: ${response.status}`,
      ),
    )
  }

  if (body?.result && !body.result.isSuccess) {
    throw new ApiError(
      200,
      body.result.code,
      sanitizeErrorMessage(body.result.message),
    )
  }

  return body as Response
}

function getJson<Response>(path: string, options?: ApiRequestOptions) {
  return requestJson<Response>(path, { ...options, method: "GET" })
}

function putJson<Response>(
  path: string,
  body: unknown,
  options?: ApiRequestOptions,
) {
  return requestJson<Response>(path, { ...options, method: "PUT", body })
}

function deleteJson<Response>(path: string, options?: ApiRequestOptions) {
  return requestJson<Response>(path, { ...options, method: "DELETE" })
}

export {
  ApiError,
  deleteJson,
  getJson,
  postJson,
  putJson,
  requestJson,
  sanitizeErrorMessage,
}
export type { ApiRequestOptions, ApiResult }
